import {
  MmmFeatureIdentity,
  MmmFeatureNameResponse
} from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';

// Delimiter used to render a feature's joined display name and to fold multiple trailing dimensions
// (e.g. tactic + brand-nonbrand) into the single tactic slot the UI shows.
export const FEATURE_NAME_DELIMITER = ' / ';

// A "raw" MMM feature id is an opaque identifier that leaked into the UI in place of a
// human-readable feature name: `feat421242`, `featid421242`, or a bare number like `421321`
// (case-insensitive). Only these are candidates for pretty-name replacement.
const RAW_FEATURE_ID = /^(?:feat(?:id)?)?\d+$/i;

export const isRawFeatureId = (value: string): boolean => RAW_FEATURE_ID.test(value.trim());

// featureIdMap keys are uppercase (e.g. "FEATID378"), but a raw id can reach the UI in any case.
// Always go through this helper so lookups match the map's key casing.
export const lookupFeatureIdentity = (
  value: string,
  map?: MmmFeatureNameResponse
): MmmFeatureIdentity | undefined => map?.featureIdMap[value.trim().toUpperCase()];

// Returns the pre-joined display name for a feature id when the map has it.
export const lookupFeatureName = (
  value: string,
  map?: MmmFeatureNameResponse
): string | undefined => lookupFeatureIdentity(value, map)?.name;

// Returns the pretty name for a raw feature id when the map has it; otherwise returns the value
// unchanged. Non-raw values pass through untouched so real names are never rewritten. Used for
// single-token labels (e.g. rMMM pairwise interaction names).
export const resolveFeatureName = (value: string, map?: MmmFeatureNameResponse): string => {
  if (!map || !isRawFeatureId(value)) return value;
  return lookupFeatureName(value, map) ?? value;
};

export const isNaSegment = (segment: string): boolean => segment.toUpperCase() === 'N/A';

// The dimension slugs ordered by the hierarchy `keys` (e.g. ['channel','platform','tactic',
// 'brand-nonbrand']). Reading order from data means a change to segment count/order in the backend
// flows through automatically instead of breaking a hardcoded positional split.
export const orderedSlugs = (map: MmmFeatureNameResponse): string[] =>
  Object.keys(map.keys).sort((a, b) => map.keys[a] - map.keys[b]);

// Resolves a row whose displayed segments are opaque ids into pretty channel/platform/tactic,
// looked up by the row's feature id (the map key). The value for each dimension comes from the
// feature's `fields` map, read in `keys` order: dimension 0 -> channel, dimension 1 -> platform,
// and every dimension from index 2 onward folds into tactic (the non-N/A values joined by
// FEATURE_NAME_DELIMITER). The UI has no dedicated Brand/Non-Brand dimension, so Brand folds into
// tactic here (shown, never silently dropped). Returns null when the display is already
// human-readable or the map has no entry, so callers keep the row's existing values. Gating on the
// displayed segments (not the feature id) means this only fires for the broken case, never
// rewriting rows that already show real names.
export const resolveFeatureParts = (
  feature: string,
  parts: { channel: string; platform: string; tactic: string },
  map?: MmmFeatureNameResponse
): { channel: string; platform: string; tactic: string } | null => {
  if (!map) return null;
  const displayIsRaw =
    isRawFeatureId(parts.channel) || isRawFeatureId(parts.platform) || isRawFeatureId(parts.tactic);
  if (!displayIsRaw) return null;
  const identity = lookupFeatureIdentity(feature, map) ?? lookupFeatureIdentity(parts.channel, map);
  if (!identity) return null;
  const slugs = orderedSlugs(map);
  const valueAt = (index: number): string => {
    const slug = slugs[index];
    return slug ? (identity.fields[slug] ?? '') : '';
  };
  const channel = valueAt(0);
  const platform = valueAt(1);
  const tactic = slugs
    .slice(2)
    .map(slug => identity.fields[slug])
    .filter(value => Boolean(value) && !isNaSegment(value))
    .join(FEATURE_NAME_DELIMITER);
  return { channel, platform, tactic };
};

// Rewrites a row's channel/platform/tactic to the pretty names for DISPLAY, preserving the original
// segments on rawChannel/rawPlatform/rawTactic so downstream code that keys the analyze payload
// (the tree's refIds) can still emit the original feature id. Rows that don't resolve are returned
// unchanged (and carry no raw* fields). Invariant: a row's channel is pretty iff rawChannel is set.
export const applyFeatureMappingToRow = <
  T extends {
    feature: string;
    channel: string;
    platform: string;
    tactic: string;
    rawChannel?: string;
    rawPlatform?: string;
    rawTactic?: string;
  }
>(
  row: T,
  map?: MmmFeatureNameResponse
): T => {
  const resolved = resolveFeatureParts(row.feature, row, map);
  if (!resolved) return row;
  return {
    ...row,
    rawChannel: row.channel,
    rawPlatform: row.platform,
    rawTactic: row.tactic,
    ...resolved
  };
};
